// apps/web/src/widgets/use-source-selection.ts
//
// Phase 11.5 — shared compose-onto-option-set null-guard for the two 11.5
// renderers (option-tier-matrix + per-option-detail-card). Both compose over a
// sibling option-set widget via `config.optionSetWidgetId`; this module lifts the
// `readSourceOptions` source-lookup + crash-guard (originally inline in
// ranked-tier/renderer.tsx:54-70) into ONE place so both renderers import the
// identical contract and Plans 02/03 can run in parallel without cross-file
// contention.
//
// `readSourceOptions` is a PURE function — NOT a hook. The sibling-state hook
// call MUST stay at each renderer's top level (Rules of Hooks: both renderers
// read a single source widget via one top-level sibling-state hook call; a
// shared hook would be valid but a shared plain fn keeps the hook usage
// unambiguous at the renderer call site). See PATTERNS §SP-5.
//
// (A ranked-tier retrofit to consume this shared helper is possible but NOT done
// here — ranked-tier keeps its in-file copy; this module is the single source for
// the 11.5 renderers.)
//
// isolatedDeclarations discipline (D-15): the explicit
// `OptionSetConfig["options"] | null` return-type annotation lets tsc emit the
// declaration without reading across module boundaries.

import type { OptionSetConfig } from "@the-recipe-spread/shared";

// Source-option-set lookup: returns the OptionSetConfig.options array, or
// null when the source widget is missing OR is the wrong template id OR has
// a malformed config (missing/non-array options field). Pass 2A guarantees
// the shape in fully-validated recipes, but the renderer must degrade
// gracefully for partially-typed callers — the Phase 8 authoring preview
// surface drives these renderers with in-progress recipes where Pass 2A may
// not have run yet, and Phase 11 AI extraction can produce option-set widgets
// with partial configs. Returning null routes those through the "source
// missing" fallback branch instead of crashing the spread render on a
// non-iterable `undefined`.
export function readSourceOptions(
  recipeWidgets: ReadonlyArray<{ instanceId: string; templateId: string; config: unknown }>,
  optionSetWidgetId: string,
): OptionSetConfig["options"] | null {
  const sourceWidget = recipeWidgets.find((w) => w.instanceId === optionSetWidgetId);
  if (sourceWidget === undefined) return null;
  if (sourceWidget.templateId !== "option-set") return null;
  // Pass 2A validates this shape; the cast here is the documented seam.
  // Defense-in-depth: structural check on `options` BEFORE returning so a
  // partial-typed config (e.g., `{ cardinality: { min: 0 } }` with no
  // `options` field) routes to the null-fallback branch instead of returning
  // undefined and crashing the downstream `for (const opt of sourceOptions)`
  // iteration. (ranked-tier WR-01 regression fix — DO NOT drop this guard.)
  const cfg = sourceWidget.config as OptionSetConfig;
  if (!Array.isArray(cfg.options)) return null;
  return cfg.options;
}
